import { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { closeCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { RedesignButton } from '@components/RedesignButton';
import { Text } from '@components/Text';
import { useHistory } from 'react-router-dom';
import { updateSiteData } from 'src/redesign/features/SelectedSite/store/actions';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import S from './TrialApproachingLimitModal.module.scss';

type TrialApproachingLimitModalProps = {
  currentOrderCount?: number;
  orderLimit?: number;
  percentageUsed?: number;
};

export const TrialApproachingLimitModal: FC<TrialApproachingLimitModalProps> = ({
  currentOrderCount = 8,
  orderLimit = 10,
  percentageUsed = 80,
}) => {
  const dispatch = useAppDispatch();
  const selectedSite = useAppSelector(getSelectedSiteDataSelector);
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const markAsSeen = async () => {
    if (!selectedSite?.id) return;

    try {
      await dispatch(
        updateSiteData({
          siteId: selectedSite.id,
          siteData: {
            has_seen_trial_approaching_modal: true,
          },
        }),
      ).unwrap();
    } catch (error) {
      console.error('Failed to update site:', error);
    }
  };

  const handleUpgrade = async () => {
    // Mark as seen in account
    await markAsSeen();
    dispatch(closeCurrentModalAction());
    history.push('/events/billing');
  };

  const handleRemindLater = async () => {
    // Mark as seen so it doesn't show again
    await markAsSeen();
    dispatch(closeCurrentModalAction());
  };

  return (
    <div className={S.trialApproachingLimitModal}>
      <div className={S.content}>
        <Text bold size="lg" className={S.title}>
          You're about to hit your trial limit
        </Text>

        <div className={S.statusBar}>
          <div className={S.statusText}>
            <Text bold className={S.statusLabel}>
              ⚠️ {currentOrderCount} of {orderLimit} orders used
            </Text>
          </div>
          <div className={S.progressBar}>
            <div className={S.progressFill} style={{ width: `${percentageUsed}%` }} />
          </div>
        </div>

        <div className={S.successSection}>
          <div className={S.successBadge}>
            🎉 You've already successfully sold {currentOrderCount} tickets
          </div>
          <Text className={S.encouragement}>You're off to a great start.</Text>
        </div>

        <Text className={S.description}>
          Your event is gaining traction — <strong>don't let sales stop now.</strong> Upgrade to
          continue accepting orders without interruption.
        </Text>

        <div className={S.upgradeCard}>
          <div className={S.optionHeader}>
            <Text bold size="md">
              Keep selling tickets
            </Text>
            <span className={S.recommendedBadge}>RECOMMENDED</span>
          </div>
          <Text className={S.urgencySubline}>Continue selling without interruption</Text>
          <ul className={S.featureList}>
            <li>Accept unlimited paid orders</li>
            <li>Keep 100% of your ticket revenue (no platform fees)</li>
            <li>Unlock seating, waitlists, and analytics</li>
            <li>Customize your event pages and checkout</li>
          </ul>
        </div>

        <div className={S.warningBox}>
          <Text className={S.warningText}>⚠️ At your limit, new orders will be paused.</Text>
        </div>
      </div>

      <div className={S.controlsContainer}>
        <button onClick={handleRemindLater} disabled={loading} className={S.remindLaterButton}>
          Remind me later
        </button>
        <RedesignButton
          onClick={handleUpgrade}
          variation="primary"
          disabled={loading}
          height={52}
          className={S.primaryButton}
        >
          <Text bold color="white-100" size="lg">
            Upgrade to keep selling
          </Text>
        </RedesignButton>
      </div>
    </div>
  );
};
